// src/services/crews.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  increment,
  type Unsubscribe,
} from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';

import { db, auth } from '../lib/firebase';
import { ensureJsAuthFromNative } from './authBridge';
import type { UserProfile, UserRelationship } from '../types';
import { View } from '../types';
import { createNotification } from './notifications';

export type CrewReactionKey = 'like' | 'love' | 'cry' | 'laugh' | 'angry' | 'wow';

const CREW_REACTION_KEYS: CrewReactionKey[] = ['like', 'love', 'cry', 'laugh', 'angry', 'wow'];

function normalizeCrewReactionKey(value: any): CrewReactionKey {
  const key = clean(value).toLowerCase();
  return (CREW_REACTION_KEYS as string[]).includes(key) ? (key as CrewReactionKey) : 'like';
}

export interface CrewChannel {
  id: string;
  relationshipId?: string;
  type: 'relationship' | 'group';
  memberIds: string[];
  memberRoles?: Record<string, 'owner' | 'moderator' | 'member' | string>;
  title?: string;
  ownerUid?: string;
  createdBy?: string;
  createdAt?: number;
  updatedAt?: number;
  description?: string;
  coverImageUrl?: string;
  coverThumbUrl?: string;
  coverImagePath?: string;
  coverImageUpdatedAt?: number;
  coverImageZoom?: number;
  coverImagePositionX?: number;
  coverImagePositionY?: number;
  status?: 'active' | 'deleted' | string;
}

export interface CrewPost {
  id: string;
  crewId: string;
  relationshipId?: string;
  authorId: string;
  authorName: string;
  authorAvatar?: string;
  text: string;
  timestamp: number;
  createdAt?: number;
  updatedAt?: number;
  likeCount?: number;
  commentCount?: number;
  status?: 'visible' | 'hidden' | string;
}

export interface CrewPostComment {
  id: string;
  crewId: string;
  postId: string;
  authorId: string;
  authorName: string;
  authorAvatar?: string;
  text: string;
  timestamp: number;
  createdAt?: number;
  updatedAt?: number;
  editedAt?: number;
  likeCount?: number;
  reactionCounts?: Partial<Record<CrewReactionKey, number>>;
  status?: 'visible' | 'hidden' | string;
}

const clean = (v: any) => String(v ?? '').trim();

const crewMutationFlights = new Map<string, Promise<unknown>>();

function runCrewMutationOnce<T>(key: string, task: () => Promise<T>): Promise<T> {
  const existing = crewMutationFlights.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = task().finally(() => {
    if (crewMutationFlights.get(key) === promise) {
      crewMutationFlights.delete(key);
    }
  });

  crewMutationFlights.set(key, promise);
  return promise;
}

const normalizeMillis = (v: any): number => {
  if (!v) return Date.now();
  if (typeof v === 'number') return v;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  return Date.now();
};

const stripUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    if ((obj as any)[key] !== undefined) out[key] = (obj as any)[key];
  });
  return out as Partial<T>;
};

const randomId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

function normalizeCrewChannel(id: string, data: any): CrewChannel {
  return {
    id,
    relationshipId: clean(data?.relationshipId) || undefined,
    type: data?.type === 'group' ? 'group' : 'relationship',
    memberIds: Array.isArray(data?.memberIds) ? data.memberIds.map(clean).filter(Boolean) : [],
    memberRoles: data?.memberRoles || {},
    title: clean(data?.title) || (data?.type === 'group' ? 'Fishing Crew' : 'Private Crew'),
    ownerUid: clean(data?.ownerUid) || undefined,
    createdBy: clean(data?.createdBy) || undefined,
    createdAt: data?.createdAt ? normalizeMillis(data.createdAt) : undefined,
    updatedAt: data?.updatedAt ? normalizeMillis(data.updatedAt) : undefined,
    description: clean(data?.description) || undefined,
    coverImageUrl: clean(data?.coverImageUrl) || undefined,
    coverThumbUrl: clean(data?.coverThumbUrl) || undefined,
    coverImagePath: clean(data?.coverImagePath) || undefined,
    coverImageUpdatedAt: data?.coverImageUpdatedAt ? normalizeMillis(data.coverImageUpdatedAt) : undefined,
    coverImageZoom: Math.min(1.6, Math.max(1, Number(data?.coverImageZoom || 1))),
    coverImagePositionX: Math.min(100, Math.max(0, Number(data?.coverImagePositionX ?? 50))),
    coverImagePositionY: Math.min(100, Math.max(0, Number(data?.coverImagePositionY ?? 50))),
    status: clean(data?.status) || 'active',
  };
}

type GroupCrewMembershipIndex = {
  crewId: string;
  uid: string;
  type: 'group';
  title: string;
  ownerUid: string;
  createdAt?: number;
  updatedAt: number;
};

const groupCrewMembershipRef = (uid: string, crewId: string) =>
  doc(db, 'users', clean(uid), 'groupCrews', clean(crewId));

function buildGroupCrewMembershipIndex(
  crewId: string,
  uid: string,
  crew: Pick<CrewChannel, 'title' | 'ownerUid' | 'createdAt' | 'updatedAt'>,
): GroupCrewMembershipIndex {
  return {
    crewId: clean(crewId),
    uid: clean(uid),
    type: 'group',
    title: clean(crew.title) || 'Fishing Crew',
    ownerUid: clean(crew.ownerUid),
    createdAt: crew.createdAt,
    updatedAt: Number(crew.updatedAt || Date.now()),
  };
}

async function syncGroupCrewMembershipIndexes(params: {
  crewId: string;
  crew: CrewChannel;
  memberIds: string[];
  removedMemberIds?: string[];
}): Promise<void> {
  const crewId = clean(params.crewId);
  if (!crewId) return;

  const members = Array.from(new Set((params.memberIds || []).map(clean).filter(Boolean)));
  const removed = Array.from(new Set((params.removedMemberIds || []).map(clean).filter(Boolean)))
    .filter((uid) => !members.includes(uid));

  const batch = writeBatch(db);

  members.forEach((memberUid) => {
    batch.set(
      groupCrewMembershipRef(memberUid, crewId),
      buildGroupCrewMembershipIndex(crewId, memberUid, params.crew),
      { merge: true },
    );
  });

  removed.forEach((memberUid) => {
    batch.delete(groupCrewMembershipRef(memberUid, crewId));
  });

  await batch.commit();
}

async function bootstrapOwnedGroupCrewMemberships(ownerUid: string): Promise<void> {
  const uid = clean(ownerUid);
  if (!uid) return;

  // One-time compatibility bridge for Stage 2 crews created before the
  // user-scoped membership index existed. The rule for /crews list access is
  // owner-scoped, so this query can only discover crews owned by this user.
  const snapshot = await getDocs(
    query(collection(db, 'crews'), where('ownerUid', '==', uid)),
  );

  for (const crewDoc of snapshot.docs) {
    const crew = normalizeCrewChannel(crewDoc.id, crewDoc.data());
    if (crew.type !== 'group' || crew.ownerUid !== uid || crew.status === 'deleted') continue;

    await syncGroupCrewMembershipIndexes({
      crewId: crew.id,
      crew,
      memberIds: crew.memberIds,
    });
  }
}

export function subscribeGroupCrews(
  uid: string,
  onChange: (crews: CrewChannel[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const currentUid = clean(uid);
  if (!currentUid) {
    onChange([]);
    return () => undefined;
  }

  let cancelled = false;
  let membershipUnsub: Unsubscribe | null = null;
  const crewUnsubs = new Map<string, Unsubscribe>();
  const crewRows = new Map<string, CrewChannel>();

  const emit = () => {
    if (cancelled) return;
    onChange(
      Array.from(crewRows.values())
        .filter((crew) => crew.type === 'group' && crew.status !== 'deleted' && crew.memberIds.includes(currentUid))
        .sort(
          (a, b) =>
            Number(b.updatedAt || b.createdAt || 0) -
            Number(a.updatedAt || a.createdAt || 0),
        ),
    );
  };

  const syncCrewDocumentListeners = (crewIds: string[]) => {
    const desired = new Set(crewIds.map(clean).filter(Boolean));

    for (const [crewId, unsubscribe] of crewUnsubs.entries()) {
      if (desired.has(crewId)) continue;
      try {
        unsubscribe();
      } catch {}
      crewUnsubs.delete(crewId);
      crewRows.delete(crewId);
    }

    desired.forEach((crewId) => {
      if (crewUnsubs.has(crewId)) return;

      const unsubscribe = onSnapshot(
        doc(db, 'crews', crewId),
        (snapshot) => {
          if (cancelled) return;

          if (!snapshot.exists()) {
            crewRows.delete(crewId);
            emit();
            return;
          }

          const crew = normalizeCrewChannel(snapshot.id, snapshot.data());
          if (crew.type === 'group' && crew.status !== 'deleted' && crew.memberIds.includes(currentUid)) {
            crewRows.set(crewId, crew);
          } else {
            crewRows.delete(crewId);
          }
          emit();
        },
        (err) => {
          if (cancelled) return;
          console.warn('[crews] group crew document subscription failed', {
            crewId,
            err,
          });
          crewRows.delete(crewId);
          onError?.(err);
          emit();
        },
      );

      crewUnsubs.set(crewId, unsubscribe);
    });

    emit();
  };

  const attach = async () => {
    const startedAt = Date.now();
    while (!auth.currentUser?.uid && Date.now() - startedAt < 2500) {
      try {
        if (Capacitor.isNativePlatform()) {
          await ensureJsAuthFromNative().catch(() => undefined);
        }
      } catch {}

      if (auth.currentUser?.uid) break;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (cancelled) return;

    const authedUid = clean(auth.currentUser?.uid);
    if (!authedUid || authedUid !== currentUid) {
      const err = new Error('Crew subscription auth is not ready for the current user');
      console.warn('[crews] subscribeGroupCrews auth not ready', {
        requestedUid: currentUid,
        authUid: authedUid || null,
      });
      onError?.(err);
      onChange([]);
      return;
    }

    // Canonical Stage 2 read path: listen only to this user's own membership
    // index. Each membership then opens the exact crew document by ID.
    membershipUnsub = onSnapshot(
      collection(db, 'users', currentUid, 'groupCrews'),
      (snapshot) => {
        const crewIds = snapshot.docs
          .map((membershipDoc) => clean((membershipDoc.data() as any)?.crewId || membershipDoc.id))
          .filter(Boolean);
        syncCrewDocumentListeners(crewIds);
      },
      (err) => {
        if (cancelled) return;
        console.warn('[crews] group crew membership subscription failed', err);
        syncCrewDocumentListeners([]);
        onError?.(err);
      },
    );

    // Self-heal crews created before the index existed. This is intentionally
    // best-effort: the live membership listener above remains the canonical path.
    try {
      await bootstrapOwnedGroupCrewMemberships(currentUid);
    } catch (err) {
      if (!cancelled) {
        console.warn('[crews] owned group crew membership backfill failed', err);
        onError?.(err);
      }
    }
  };

  void attach().catch((err) => {
    if (cancelled) return;
    console.warn('[crews] subscribeGroupCrews attach failed', err);
    onError?.(err);
    onChange([]);
  });

  return () => {
    cancelled = true;
    try {
      membershipUnsub?.();
    } catch {}
    membershipUnsub = null;

    crewUnsubs.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch {}
    });
    crewUnsubs.clear();
    crewRows.clear();
  };
}

export async function createGroupCrew(params: {
  owner: UserProfile;
  title: string;
  memberIds?: string[];
}): Promise<string> {
  await ensureAuthedForWrite('createGroupCrew');

  const ownerUid = clean((params.owner as any)?.uid || auth.currentUser?.uid);
  const title = clean(params.title).slice(0, 48);
  if (!ownerUid || !title) throw new Error('Crew name is required');

  const memberIds = Array.from(
    new Set([ownerUid, ...(Array.isArray(params.memberIds) ? params.memberIds : [])].map(clean).filter(Boolean)),
  ).slice(0, 25);

  const createKey = `group:create:${ownerUid}:${title.toLowerCase()}:${memberIds.slice().sort().join(',')}`;
  return runCrewMutationOnce(createKey, async () => {
    const crewId = `group_${ownerUid.slice(0, 8)}_${randomId().replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 24)}`;
    const now = Date.now();
    const memberRoles = memberIds.reduce(
      (acc, id) => ({ ...acc, [id]: id === ownerUid ? 'owner' : 'member' }),
      {} as Record<string, string>,
    );

    const crew: CrewChannel = {
      id: crewId,
      type: 'group',
      title,
      ownerUid,
      createdBy: ownerUid,
      memberIds,
      memberRoles,
      createdAt: now,
      updatedAt: now,
      description: '',
      status: 'active',
    };

    // Phase 1: create the authoritative crew document.
    await setDoc(doc(db, 'crews', crewId), {
      ...crew,
      updatedAtServer: serverTimestamp(),
    });

    // Phase 2: publish deterministic per-user membership indexes. If this step is
    // interrupted, bootstrapOwnedGroupCrewMemberships() repairs it next Dock open.
    await syncGroupCrewMembershipIndexes({
      crewId,
      crew,
      memberIds,
    });

    return crewId;
  });
}

export async function updateGroupCrewMembers(
  crewId: string,
  ownerUid: string,
  memberIds: string[],
): Promise<void> {
  await ensureAuthedForWrite('updateGroupCrewMembers');

  const id = clean(crewId);
  const owner = clean(ownerUid || auth.currentUser?.uid);
  if (!id || !owner) throw new Error('Missing crew fields');

  const crewRef = doc(db, 'crews', id);
  const existing = await getDoc(crewRef);
  if (!existing.exists()) throw new Error('Crew not found');

  const existingCrew = normalizeCrewChannel(existing.id, existing.data());
  if (existingCrew.type !== 'group' || clean(existingCrew.ownerUid) !== owner) {
    throw new Error('Only the group crew owner can update members');
  }

  const normalized = Array.from(
    new Set([owner, ...(Array.isArray(memberIds) ? memberIds : [])].map(clean).filter(Boolean)),
  ).slice(0, 25);

  const previousRoles = existingCrew.memberRoles || {};
  const memberRoles = normalized.reduce(
    (acc, uid) => ({
      ...acc,
      [uid]: uid === owner
        ? 'owner'
        : previousRoles[uid] === 'moderator'
          ? 'moderator'
          : 'member',
    }),
    {} as Record<string, string>,
  );

  const now = Date.now();
  await updateDoc(crewRef, {
    memberIds: normalized,
    memberRoles,
    status: existingCrew.status || 'active',
    updatedAt: now,
    updatedAtServer: serverTimestamp(),
  });

  const nextCrew: CrewChannel = {
    ...existingCrew,
    memberIds: normalized,
    memberRoles,
    updatedAt: now,
  };

  const removedMemberIds = existingCrew.memberIds.filter(
    (memberUid) => !normalized.includes(memberUid),
  );

  await syncGroupCrewMembershipIndexes({
    crewId: id,
    crew: nextCrew,
    memberIds: normalized,
    removedMemberIds,
  });
}


export async function updateGroupCrewProfile(params: {
  crewId: string;
  ownerUid: string;
  title: string;
  description?: string;
  coverImageUrl?: string | null;
  coverThumbUrl?: string | null;
  coverImagePath?: string | null;
  coverImageUpdatedAt?: number | null;
  coverImageZoom?: number;
  coverImagePositionX?: number;
  coverImagePositionY?: number;
}): Promise<void> {
  await ensureAuthedForWrite('updateGroupCrewProfile');

  const crewId = clean(params.crewId);
  const ownerUid = clean(params.ownerUid || auth.currentUser?.uid);
  const title = clean(params.title).slice(0, 48);
  const description = clean(params.description).slice(0, 180);
  if (!crewId || !ownerUid || !title) throw new Error('Missing crew profile fields');

  const ref = doc(db, 'crews', crewId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Crew not found');
  const crew = normalizeCrewChannel(snap.id, snap.data());
  if (crew.type !== 'group' || crew.ownerUid !== ownerUid || crew.status === 'deleted') {
    throw new Error('Only the crew owner can edit this crew');
  }

  const now = Date.now();
  const coverImageZoom = Math.min(1.6, Math.max(1, Number(params.coverImageZoom ?? crew.coverImageZoom ?? 1)));
  const coverImagePositionX = Math.min(100, Math.max(0, Number(params.coverImagePositionX ?? crew.coverImagePositionX ?? 50)));
  const coverImagePositionY = Math.min(100, Math.max(0, Number(params.coverImagePositionY ?? crew.coverImagePositionY ?? 50)));
  const patch = stripUndefined({
    title,
    description,
    coverImageUrl: params.coverImageUrl,
    coverThumbUrl: params.coverThumbUrl,
    coverImagePath: params.coverImagePath,
    coverImageUpdatedAt: params.coverImageUpdatedAt,
    coverImageZoom,
    coverImagePositionX,
    coverImagePositionY,
    status: crew.status || 'active',
    updatedAt: now,
    updatedAtServer: serverTimestamp(),
  });
  await updateDoc(ref, patch);

  await syncGroupCrewMembershipIndexes({
    crewId,
    crew: {
      ...crew,
      title,
      description,
      coverImageUrl: params.coverImageUrl === undefined ? crew.coverImageUrl : params.coverImageUrl || undefined,
      coverThumbUrl: params.coverThumbUrl === undefined ? crew.coverThumbUrl : params.coverThumbUrl || undefined,
      coverImagePath: params.coverImagePath === undefined ? crew.coverImagePath : params.coverImagePath || undefined,
      coverImageUpdatedAt: params.coverImageUpdatedAt === undefined ? crew.coverImageUpdatedAt : Number(params.coverImageUpdatedAt || now),
      coverImageZoom,
      coverImagePositionX,
      coverImagePositionY,
      updatedAt: now,
    },
    memberIds: crew.memberIds,
  });
}

export async function setGroupCrewMemberRole(params: {
  crewId: string;
  ownerUid: string;
  memberUid: string;
  role: 'moderator' | 'member';
}): Promise<void> {
  await ensureAuthedForWrite('setGroupCrewMemberRole');

  const crewId = clean(params.crewId);
  const ownerUid = clean(params.ownerUid || auth.currentUser?.uid);
  const memberUid = clean(params.memberUid);
  if (!crewId || !ownerUid || !memberUid || memberUid === ownerUid) {
    throw new Error('Invalid crew role change');
  }

  const ref = doc(db, 'crews', crewId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Crew not found');
  const crew = normalizeCrewChannel(snap.id, snap.data());
  if (crew.type !== 'group' || crew.ownerUid !== ownerUid || crew.status === 'deleted') {
    throw new Error('Only the crew owner can manage roles');
  }
  if (!crew.memberIds.includes(memberUid)) throw new Error('Crew member not found');

  const memberRoles = { ...(crew.memberRoles || {}) };
  memberRoles[ownerUid] = 'owner';
  memberRoles[memberUid] = params.role === 'moderator' ? 'moderator' : 'member';
  const now = Date.now();

  await updateDoc(ref, {
    memberRoles,
    status: crew.status || 'active',
    updatedAt: now,
    updatedAtServer: serverTimestamp(),
  });
}

export async function leaveGroupCrew(crewId: string, uid: string): Promise<void> {
  await ensureAuthedForWrite('leaveGroupCrew');

  const id = clean(crewId);
  const memberUid = clean(uid || auth.currentUser?.uid);
  if (!id || !memberUid) throw new Error('Missing crew leave fields');

  const ref = doc(db, 'crews', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const crew = normalizeCrewChannel(snap.id, snap.data());
  if (crew.type !== 'group' || crew.status === 'deleted') return;
  if (crew.ownerUid === memberUid) throw new Error('Transfer ownership or delete the crew instead');
  if (!crew.memberIds.includes(memberUid)) return;

  const memberIds = crew.memberIds.filter((id) => id !== memberUid);
  const memberRoles = { ...(crew.memberRoles || {}) };
  delete memberRoles[memberUid];
  const now = Date.now();

  await updateDoc(ref, {
    memberIds,
    memberRoles,
    status: crew.status || 'active',
    updatedAt: now,
    updatedAtServer: serverTimestamp(),
  });

  await writeBatch(db)
    .delete(groupCrewMembershipRef(memberUid, id))
    .commit();
}

export async function deleteGroupCrew(crewId: string, ownerUid: string): Promise<void> {
  await ensureAuthedForWrite('deleteGroupCrew');

  const id = clean(crewId);
  const owner = clean(ownerUid || auth.currentUser?.uid);
  if (!id || !owner) throw new Error('Missing crew delete fields');

  const ref = doc(db, 'crews', id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const crew = normalizeCrewChannel(snap.id, snap.data());
  if (crew.type !== 'group' || crew.ownerUid !== owner) {
    throw new Error('Only the crew owner can delete this crew');
  }

  const now = Date.now();
  // Soft-delete the authoritative crew first so nested board content becomes
  // inaccessible immediately without leaving recursively-deleted orphan data.
  await updateDoc(ref, {
    status: 'deleted',
    deletedAt: now,
    deletedBy: owner,
    updatedAt: now,
    updatedAtServer: serverTimestamp(),
  });

  const batch = writeBatch(db);
  crew.memberIds.forEach((memberUid) => {
    batch.delete(groupCrewMembershipRef(memberUid, id));
  });
  await batch.commit();
}

async function ensureAuthedForWrite(label: string) {
  try {
    const native = (() => {
      try {
        return Capacitor.isNativePlatform();
      } catch {
        return false;
      }
    })();

    if (!native) return;
    if (auth.currentUser?.uid) return;

    await Promise.race([
      ensureJsAuthFromNative(),
      new Promise((resolve) => setTimeout(resolve, 2500)),
    ]);
  } catch (e) {
    console.warn(`[crews] ensureAuthedForWrite(${label}) failed`, e);
  }
}

const displayNameFor = (user: any, uid = '') =>
  clean(user?.name) ||
  clean(user?.displayName) ||
  clean(user?.username) ||
  clean(user?.handle) ||
  (uid ? `Angler ${uid.slice(0, 4)}` : 'Angler');

const avatarFor = (user: any) =>
  clean(user?.avatar) || clean(user?.avatarUrl) || clean(user?.photoURL) || '/avatar-default.png';

async function notifyCrewActivity(params: {
  crewId: string;
  actor: UserProfile;
  kind: 'post' | 'reply';
  postId: string;
  preview: string;
}): Promise<void> {
  const crewId = clean(params.crewId);
  const actorUid = clean((params.actor as any)?.uid || auth.currentUser?.uid);
  if (!crewId || !actorUid) return;

  const crewSnap = await getDoc(doc(db, 'crews', crewId));
  if (!crewSnap.exists()) return;

  const crewData = crewSnap.data() as any;
  if (clean(crewData?.status) === 'deleted') return;

  const memberIds: string[] = Array.isArray(crewData?.memberIds)
  ? Array.from(
      new Set<string>(
        crewData.memberIds
          .map((value: unknown) => clean(value))
          .filter((value: string) => Boolean(value)),
      ),
    )
  : [];

const recipients: string[] = memberIds.filter(
  (memberUid) => memberUid !== actorUid,
);
  if (!recipients.length) return;

  const actorName = displayNameFor(params.actor, actorUid);
  const actorAvatar = avatarFor(params.actor);
  const crewTitle =
    clean(crewData?.title) ||
    (crewData?.type === 'group' ? 'your crew' : 'your private crew');
  const preview = clean(params.preview).slice(0, 96);

  await Promise.allSettled(
    recipients.map((recipientUid) =>
      createNotification({
        userId: recipientUid,
        type: params.kind === 'post' ? 'CREW_POST' : 'CREW_REPLY',
        title: params.kind === 'post' ? `New post in ${crewTitle}` : `New reply in ${crewTitle}`,
        body:
          params.kind === 'post'
            ? `${actorName} posted${preview ? `: “${preview}”` : '.'}`
            : `${actorName} replied${preview ? `: “${preview}”` : '.'}`,
        actor: {
          uid: actorUid,
          name: actorName,
          avatar: actorAvatar,
        },
        entity: {
          postId: params.postId,
          userId: actorUid,
        },
        link: {
          view: View.Crew,
          targetId: crewId,
          subTargetId: params.postId,
        },
      }),
    ),
  );
}

export async function ensureCrewChannelFromRelationship(
  relationship: UserRelationship,
  currentUser: UserProfile,
  otherUser?: any,
): Promise<string> {
  await ensureAuthedForWrite('ensureCrewChannelFromRelationship');

  const currentUid = clean((currentUser as any)?.uid || auth.currentUser?.uid);
  const relationshipId = clean(relationship?.id);
  const memberIds = (Array.isArray(relationship?.participantIds)
    ? relationship.participantIds
    : [relationship?.requesterId, relationship?.recipientId]
  ).map(clean).filter(Boolean).sort();

  if (!currentUid || !relationshipId || relationship?.status !== 'accepted' || memberIds.length !== 2) {
    throw new Error('Invalid accepted crew relationship');
  }

  const otherUid = memberIds.find((id) => id !== currentUid) || '';
  const otherName = displayNameFor(otherUser, otherUid);
  const ref = doc(db, 'crews', relationshipId);
  const existing = await getDoc(ref);
  const now = Date.now();

  await setDoc(ref, stripUndefined({
    id: relationshipId,
    relationshipId,
    type: 'relationship',
    memberIds,
    memberRoles: memberIds.reduce((acc, id) => ({ ...acc, [id]: 'member' }), {} as Record<string, string>),
    title: `${otherName} Crew`,
    createdBy: existing.exists() ? (existing.data() as any)?.createdBy || currentUid : currentUid,
    createdAt: existing.exists() ? normalizeMillis((existing.data() as any)?.createdAt) : now,
    updatedAt: now,
    updatedAtServer: serverTimestamp(),
  }), { merge: true });

  return relationshipId;
}

function normalizeCrewPost(id: string, data: any): CrewPost {
  return {
    id,
    crewId: clean(data?.crewId),
    relationshipId: clean(data?.relationshipId),
    authorId: clean(data?.authorId),
    authorName: clean(data?.authorName) || 'Angler',
    authorAvatar: clean(data?.authorAvatar) || '/avatar-default.png',
    text: clean(data?.text),
    timestamp: normalizeMillis(data?.timestamp ?? data?.createdAt),
    createdAt: normalizeMillis(data?.createdAt ?? data?.timestamp),
    updatedAt: data?.updatedAt ? normalizeMillis(data.updatedAt) : undefined,
    likeCount: Number(data?.likeCount || 0),
    commentCount: Number(data?.commentCount || 0),
    status: data?.status || 'visible',
  };
}

function normalizeCrewComment(id: string, data: any): CrewPostComment {
  return {
    id,
    crewId: clean(data?.crewId),
    postId: clean(data?.postId),
    authorId: clean(data?.authorId),
    authorName: clean(data?.authorName) || 'Angler',
    authorAvatar: clean(data?.authorAvatar) || '/avatar-default.png',
    text: clean(data?.text),
    timestamp: normalizeMillis(data?.timestamp ?? data?.createdAt),
    createdAt: normalizeMillis(data?.createdAt ?? data?.timestamp),
    updatedAt: data?.updatedAt ? normalizeMillis(data.updatedAt) : undefined,
    likeCount: Number(data?.likeCount || 0),
    reactionCounts: data?.reactionCounts || {},
    status: data?.status || 'visible',
  };
}

export function subscribeCrewPosts(
  crewId: string,
  onPosts: (posts: CrewPost[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const id = clean(crewId);
  if (!id) {
    onPosts([]);
    return () => undefined;
  }

  const qRef = query(collection(db, 'crews', id, 'posts'), orderBy('timestamp', 'desc'), limit(60));
  return onSnapshot(
    qRef,
    (snap) => {
      onPosts(snap.docs.map((d) => normalizeCrewPost(d.id, d.data())).filter((p) => p.status !== 'hidden'));
    },
    (err) => {
      console.warn('[crews] subscribeCrewPosts failed', err);
      onError?.(err);
    },
  );
}

export async function getCrewPostById(
  crewId: string,
  postId: string,
): Promise<CrewPost | null> {
  const cId = clean(crewId);
  const pId = clean(postId);
  if (!cId || !pId) return null;

  await ensureAuthedForWrite('getCrewPostById');

  const snap = await getDoc(doc(db, 'crews', cId, 'posts', pId));
  if (!snap.exists()) return null;

  const post = normalizeCrewPost(snap.id, snap.data());
  return post.status === 'hidden' ? null : post;
}

export function subscribeCrewPostComments(
  crewId: string,
  postId: string,
  onComments: (comments: CrewPostComment[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const cId = clean(crewId);
  const pId = clean(postId);
  if (!cId || !pId) {
    onComments([]);
    return () => undefined;
  }

  const qRef = query(collection(db, 'crews', cId, 'posts', pId, 'comments'), orderBy('timestamp', 'asc'), limit(80));
  return onSnapshot(
    qRef,
    (snap) => {
      onComments(snap.docs.map((d) => normalizeCrewComment(d.id, d.data())).filter((c) => c.status !== 'hidden'));
    },
    (err) => {
      console.warn('[crews] subscribeCrewPostComments failed', err);
      onError?.(err);
    },
  );
}

export async function createCrewPost(crewId: string, currentUser: UserProfile, text: string): Promise<string> {
  await ensureAuthedForWrite('createCrewPost');

  const cId = clean(crewId);
  const uid = clean((currentUser as any)?.uid || auth.currentUser?.uid);
  const body = clean(text);
  if (!cId || !uid || !body) throw new Error('Missing crew post fields');

  const createKey = `post:create:${cId}:${uid}:${body}`;
  return runCrewMutationOnce(createKey, async () => {
    const postId = randomId();
    const now = Date.now();
    const ref = doc(db, 'crews', cId, 'posts', postId);

    await setDoc(ref, stripUndefined({
      id: postId,
      crewId: cId,
      relationshipId: cId,
      authorId: uid,
      authorName: displayNameFor(currentUser, uid),
      authorAvatar: avatarFor(currentUser),
      text: body,
      timestamp: now,
      createdAt: now,
      updatedAt: now,
      likeCount: 0,
      reactionCounts: {},
      commentCount: 0,
      status: 'visible',
    }));

    await updateDoc(doc(db, 'crews', cId), { updatedAt: now, updatedAtServer: serverTimestamp() }).catch(() => undefined);

    void notifyCrewActivity({
      crewId: cId,
      actor: currentUser,
      kind: 'post',
      postId,
      preview: body,
    }).catch((err) => console.warn('[crews] crew post notification skipped', err));

    return postId;
  });
}

export async function deleteCrewPost(crewId: string, postId: string): Promise<void> {
  await ensureAuthedForWrite('deleteCrewPost');
  const cId = clean(crewId);
  const pId = clean(postId);
  if (!cId || !pId) throw new Error('Missing crew post id');
  await updateDoc(doc(db, 'crews', cId, 'posts', pId), {
    status: 'hidden',
    hiddenAt: Date.now(),
    updatedAt: Date.now(),
  });
}

export async function updateCrewPost(crewId: string, postId: string, text: string): Promise<void> {
  await ensureAuthedForWrite('updateCrewPost');

  const cId = clean(crewId);
  const pId = clean(postId);
  const body = clean(text);
  if (!cId || !pId || !body) throw new Error('Missing crew post edit fields');

  await updateDoc(doc(db, 'crews', cId, 'posts', pId), {
    text: body,
    editedAt: Date.now(),
    updatedAt: Date.now(),
  });
}

export function subscribeCrewPostLikeByMe(
  crewId: string,
  postId: string,
  uid: string,
  onReaction: (reaction: CrewReactionKey | null) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const cId = clean(crewId);
  const pId = clean(postId);
  const userId = clean(uid);
  if (!cId || !pId || !userId) {
    onReaction(null);
    return () => undefined;
  }

  const ref = doc(db, 'crews', cId, 'posts', pId, 'likes', userId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onReaction(null);
        return;
      }
      onReaction(normalizeCrewReactionKey((snap.data() as any)?.type));
    },
    (err) => {
      console.warn('[crews] subscribeCrewPostLikeByMe failed', err);
      onReaction(null);
      onError?.(err);
    },
  );
}

export function subscribeCrewPostCommentLikeByMe(
  crewId: string,
  postId: string,
  commentId: string,
  uid: string,
  onReaction: (reaction: CrewReactionKey | null) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const cId = clean(crewId);
  const pId = clean(postId);
  const cmId = clean(commentId);
  const userId = clean(uid);
  if (!cId || !pId || !cmId || !userId) {
    onReaction(null);
    return () => undefined;
  }

  const ref = doc(db, 'crews', cId, 'posts', pId, 'comments', cmId, 'likes', userId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onReaction(null);
        return;
      }
      onReaction(normalizeCrewReactionKey((snap.data() as any)?.type));
    },
    (err) => {
      console.warn('[crews] subscribeCrewPostCommentLikeByMe failed', err);
      onReaction(null);
      onError?.(err);
    },
  );
}

export async function addCrewPostComment(
  crewId: string,
  postId: string,
  currentUser: UserProfile,
  text: string,
): Promise<string> {
  await ensureAuthedForWrite('addCrewPostComment');

  const cId = clean(crewId);
  const pId = clean(postId);
  const uid = clean((currentUser as any)?.uid || auth.currentUser?.uid);
  const body = clean(text);
  if (!cId || !pId || !uid || !body) throw new Error('Missing crew comment fields');

  const createKey = `comment:create:${cId}:${pId}:${uid}:${body}`;
  return runCrewMutationOnce(createKey, async () => {
    const commentId = randomId();
    const now = Date.now();
    const batch = writeBatch(db);
    const commentRef = doc(db, 'crews', cId, 'posts', pId, 'comments', commentId);
    const postRef = doc(db, 'crews', cId, 'posts', pId);

    batch.set(commentRef, stripUndefined({
      id: commentId,
      crewId: cId,
      postId: pId,
      authorId: uid,
      authorName: displayNameFor(currentUser, uid),
      authorAvatar: avatarFor(currentUser),
      text: body,
      timestamp: now,
      createdAt: now,
      updatedAt: now,
      likeCount: 0,
      reactionCounts: {},
      status: 'visible',
    }));
    batch.update(postRef, { commentCount: increment(1), lastCommentAt: now, updatedAt: now });
    await batch.commit();

    void notifyCrewActivity({
      crewId: cId,
      actor: currentUser,
      kind: 'reply',
      postId: pId,
      preview: body,
    }).catch((err) => console.warn('[crews] crew reply notification skipped', err));

    return commentId;
  });
}

export async function updateCrewPostComment(
  crewId: string,
  postId: string,
  commentId: string,
  text: string,
): Promise<void> {
  await ensureAuthedForWrite('updateCrewPostComment');

  const cId = clean(crewId);
  const pId = clean(postId);
  const cmId = clean(commentId);
  const body = clean(text);
  if (!cId || !pId || !cmId || !body) throw new Error('Missing crew comment edit fields');

  await updateDoc(doc(db, 'crews', cId, 'posts', pId, 'comments', cmId), {
    text: body,
    editedAt: Date.now(),
    updatedAt: Date.now(),
  });
}

export async function deleteCrewPostComment(
  crewId: string,
  postId: string,
  commentId: string,
): Promise<void> {
  await ensureAuthedForWrite('deleteCrewPostComment');

  const cId = clean(crewId);
  const pId = clean(postId);
  const cmId = clean(commentId);
  if (!cId || !pId || !cmId) throw new Error('Missing crew comment delete fields');

  const now = Date.now();
  const batch = writeBatch(db);
  batch.update(doc(db, 'crews', cId, 'posts', pId, 'comments', cmId), {
    status: 'hidden',
    hiddenAt: now,
    updatedAt: now,
  });
  batch.update(doc(db, 'crews', cId, 'posts', pId), {
    commentCount: increment(-1),
    updatedAt: now,
  });
  await batch.commit();
}

export async function toggleCrewPostLike(
  crewId: string,
  postId: string,
  uid: string,
  reaction: CrewReactionKey = 'like',
): Promise<CrewReactionKey | null> {
  await ensureAuthedForWrite('toggleCrewPostLike');

  const cId = clean(crewId);
  const pId = clean(postId);
  const userId = clean(uid || auth.currentUser?.uid);
  const nextReaction = normalizeCrewReactionKey(reaction);
  if (!cId || !pId || !userId) throw new Error('Missing crew like fields');

  const postRef = doc(db, 'crews', cId, 'posts', pId);
  const likeRef = doc(db, 'crews', cId, 'posts', pId, 'likes', userId);

  return runTransaction(db, async (tx) => {
    const [postSnap, likeSnap] = await Promise.all([tx.get(postRef), tx.get(likeRef)]);
    if (!postSnap.exists()) throw new Error('Crew post missing');

    const now = Date.now();

    if (likeSnap.exists()) {
      const prevReaction = normalizeCrewReactionKey((likeSnap.data() as any)?.type);

      if (prevReaction === nextReaction) {
        tx.delete(likeRef);
        tx.update(postRef, {
          likeCount: increment(-1),
          [`reactionCounts.${prevReaction}`]: increment(-1),
          updatedAt: now,
        });
        return null;
      }

      tx.update(likeRef, { type: nextReaction, updatedAt: now });
      tx.update(postRef, {
        [`reactionCounts.${prevReaction}`]: increment(-1),
        [`reactionCounts.${nextReaction}`]: increment(1),
        updatedAt: now,
      });
      return nextReaction;
    }

    tx.set(likeRef, { uid: userId, type: nextReaction, createdAt: now, updatedAt: now });
    tx.update(postRef, {
      likeCount: increment(1),
      [`reactionCounts.${nextReaction}`]: increment(1),
      updatedAt: now,
    });
    return nextReaction;
  });
}


export async function toggleCrewPostCommentLike(
  crewId: string,
  postId: string,
  commentId: string,
  uid: string,
  reaction: CrewReactionKey = 'like',
): Promise<CrewReactionKey | null> {
  await ensureAuthedForWrite('toggleCrewPostCommentLike');

  const cId = clean(crewId);
  const pId = clean(postId);
  const cmId = clean(commentId);
  const userId = clean(uid || auth.currentUser?.uid);
  const nextReaction = normalizeCrewReactionKey(reaction);
  if (!cId || !pId || !cmId || !userId) throw new Error('Missing crew comment reaction fields');

  const commentRef = doc(db, 'crews', cId, 'posts', pId, 'comments', cmId);
  const likeRef = doc(db, 'crews', cId, 'posts', pId, 'comments', cmId, 'likes', userId);

  return runTransaction(db, async (tx) => {
    const [commentSnap, likeSnap] = await Promise.all([tx.get(commentRef), tx.get(likeRef)]);
    if (!commentSnap.exists()) throw new Error('Crew comment missing');

    const now = Date.now();

    if (likeSnap.exists()) {
      const prevReaction = normalizeCrewReactionKey((likeSnap.data() as any)?.type);

      if (prevReaction === nextReaction) {
        tx.delete(likeRef);
        tx.update(commentRef, {
          likeCount: increment(-1),
          [`reactionCounts.${prevReaction}`]: increment(-1),
          updatedAt: now,
        });
        return null;
      }

      tx.update(likeRef, { type: nextReaction, updatedAt: now });
      tx.update(commentRef, {
        [`reactionCounts.${prevReaction}`]: increment(-1),
        [`reactionCounts.${nextReaction}`]: increment(1),
        updatedAt: now,
      });
      return nextReaction;
    }

    tx.set(likeRef, { uid: userId, type: nextReaction, createdAt: now, updatedAt: now });
    tx.update(commentRef, {
      likeCount: increment(1),
      [`reactionCounts.${nextReaction}`]: increment(1),
      updatedAt: now,
    });
    return nextReaction;
  });
}
